import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import { DEMO_RESOLUTION } from "../../lib/demoContent";
import { SceneCaption } from "./SceneCaption";

const FloorCard = ({ kind, spec, floor, annualFloor, delay }) => {
  const isBefore = kind === "before";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className="border bg-white rounded-md p-6 xl:p-7"
      style={{
        borderColor: isBefore ? "#DDD9CF" : "#C4521E",
        backgroundColor: isBefore ? "#FFFFFF" : "#FBE4D8",
      }}
    >
      <div
        className="text-[10px] uppercase tracking-[0.20em]"
        style={{ color: isBefore ? "#56534A" : "#C4521E" }}
      >
        {isBefore ? "Before" : "After"}
      </div>
      <p className="mt-3 text-sm xl:text-base text-[#1A1814] leading-relaxed">
        {spec}
      </p>
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#56534A]">
          Value floor
        </div>
        <div
          className="font-serif-display text-3xl xl:text-4xl mt-1 tabular-nums"
          style={{ color: isBefore ? "#1A1814" : "#C4521E" }}
        >
          ${floor.toLocaleString()}
          <span className="text-base text-[#56534A] font-sans"> / month</span>
        </div>
        <div className="text-xs text-[#56534A] mt-1 tabular-nums">
          ${annualFloor.toLocaleString()} / year
        </div>
      </div>
    </motion.div>
  );
};

/** Scene 6 — Maya's resolution. Before / After in matching cards. */
export const ScreenResolution = () => (
  <div className="max-w-4xl" data-testid="demo-scene-resolution">
    <SceneCaption
      eyebrow="The decision"
      body="Maya didn't kill the feature. She redesigned the spec."
    />

    <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch gap-4 xl:gap-5">
      <FloorCard
        kind="before"
        spec={DEMO_RESOLUTION.before.spec}
        floor={DEMO_RESOLUTION.before.floor}
        annualFloor={DEMO_RESOLUTION.before.annualFloor}
        delay={0.7}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.3 }}
        className="hidden md:flex items-center justify-center"
      >
        <ArrowRight size={28} strokeWidth={1.5} className="text-[#C4521E]" />
      </motion.div>

      <FloorCard
        kind="after"
        spec={DEMO_RESOLUTION.after.spec}
        floor={DEMO_RESOLUTION.after.floor}
        annualFloor={DEMO_RESOLUTION.after.annualFloor}
        delay={1.6}
      />
    </div>

    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 2.6, ease: [0.22, 1, 0.36, 1] }}
      className="mt-6 border border-[#DDD9CF] bg-white rounded-md px-6 py-5 xl:px-7 xl:py-6"
    >
      <p className="text-base xl:text-lg text-[#1A1814] leading-relaxed">
        A defensible spec. Briefed to engineering with confidence — not after
        the first $50,000 bill.
      </p>
    </motion.div>

    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 3.6 }}
      className="mt-8 font-serif-display text-2xl xl:text-3xl text-[#1A1814] text-center"
    >
      What would you discover?
    </motion.p>
  </div>
);
